import { PermissionsType } from '@/common/permissionsConst';
import { useHasPermission } from '@/utils/utils';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import {
  Button,
  DatePicker,
  Form,
  FormTitle,
  message,
  Modal,
  Select,
  useBreadcrumbRoutes,
  Wrapper,
  Radio,
} from '@maxtropy/components';
import {
  apiV2EnergyReportCostCalcPost,
  apiV2EnergyReportMonthCycleDetailPost,
  apiV2EnergyReportTotalConfigDetailPost,
} from '@maxtropy/device-customer-apis-v2';
import { Anchor, RadioChangeEvent, Space } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import qs from 'qs';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { DateType, DateTypeDisplay } from './const';
import Air from './components/Air';
import Cost from './components/Cost';
import Electricity from './components/Electricity';
import MonthCycleModal from './components/MonthCycleModal';
import Overview from './components/Overview';
import Steam from './components/Steam';
import Water from './components/Water';
import styles from './index.module.scss';
import { GasType } from './components/Air/useGetGasData';
import { useRequest } from 'ahooks';
import { createContext } from 'react';
import { getTimeRangeByMonthPeriod } from './utils';

const { RangePicker } = DatePicker;

// 报表范围类型
enum ReportScope {
  FACTORY = 'factory', // 全厂
  DEPT = 'dept', // 部门
  AREA = 'area', // 区域
}

// 月周期类型
enum MonthPeriodType {
  NATURAL = 'natural', // 自然月
  CUSTOM = 'custom', // 自定义（如26日~25日）
}

export interface MonthPeriodContextValue {
  reportScope: ReportScope;
  selectedDeptAreaId?: number;
  monthPeriodType: MonthPeriodType;
  monthPeriodStart: number;
  monthPeriodEnd: number;
  getMonthPeriodRange: (monthDate: Dayjs) => { startTime: number; endTime: number };
  getYearPeriodRange: (yearDate: Dayjs) => { startTime: number; endTime: number };
  monthCycleApiParams?: {
    deptAreaId?: number;
    monthCycleType: number;
    monthCycleFrom: number;
    monthCycleTo: number;
  };
}

export const MonthPeriodContext = createContext<MonthPeriodContextValue>({
  reportScope: ReportScope.FACTORY,
  monthPeriodType: MonthPeriodType.CUSTOM,
  monthPeriodStart: 26,
  monthPeriodEnd: 25,
  getMonthPeriodRange: (monthDate: Dayjs) => ({
    startTime: monthDate.startOf('month').valueOf(),
    endTime: monthDate.endOf('month').valueOf(),
  }),
  getYearPeriodRange: (yearDate: Dayjs) => ({
    startTime: yearDate.startOf('year').valueOf(),
    endTime: yearDate.endOf('year').valueOf(),
  }),
});

type RangeValue = [Dayjs | null, Dayjs | null] | null;

// 获取月周期的起止日（如 26~25 表示上月26日~本月25日）
const getMonthPeriodRange = (
  monthDate: Dayjs,
  startDay: number,
  endDay: number,
  isNatural: boolean
): { startTime: number; endTime: number } => {
  if (isNatural) {
    return {
      startTime: monthDate.startOf('month').valueOf(),
      endTime: monthDate.endOf('month').valueOf(),
    };
  }
  // 自定义周期：如 startDay=26, endDay=25
  // 本月周期 = 上月startDay日 ~ 本月endDay日
  const periodStart = monthDate.clone().subtract(1, 'month').date(startDay).startOf('day');
  const periodEnd = monthDate.clone().date(endDay).endOf('day');
  return {
    startTime: periodStart.valueOf(),
    endTime: periodEnd.valueOf(),
  };
};

// 获取年周期的起止时间
const getYearPeriodRange = (
  yearDate: Dayjs,
  startDay: number,
  endDay: number,
  isNatural: boolean
): { startTime: number; endTime: number } => {
  if (isNatural) {
    return {
      startTime: yearDate.startOf('year').valueOf(),
      endTime: yearDate.endOf('year').valueOf(),
    };
  }
  // 自定义周期：如 startDay=26, endDay=25
  // 本年周期 = 上年12月startDay日 ~ 本年12月endDay日
  const periodStart = yearDate.clone().subtract(1, 'year').month(11).date(startDay).startOf('day');
  const periodEnd = yearDate.clone().month(11).date(endDay).endOf('day');
  return {
    startTime: periodStart.valueOf(),
    endTime: periodEnd.valueOf(),
  };
};

const EnergyStatisticalReport = () => {
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const [calcModalOpen, setCalcModalOpen] = useState<boolean>(false);
  const [form] = Form.useForm();
  const [calcModalValue, setCalcModalValue] = useState<RangeValue>(null);
  const [calcModalDates, setCalcModalDates] = useState<RangeValue>();
  const [hasConfigGas, setHasConfigGas] = useState<GasType[]>();
  const hasRecalPermission = useHasPermission(PermissionsType.B_RECALCULATE);
  const hasConfigPermission = useHasPermission(PermissionsType.B_SETTINGSPAGE);
  const rightColRef = useRef<HTMLDivElement | null>(null);

  // 报表范围（全厂/部门/区域）
  const [reportScope, setReportScope] = useState<ReportScope>(ReportScope.FACTORY);
  // 选中的部门/区域 ID
  const [selectedDeptAreaId, setSelectedDeptAreaId] = useState<number>();

  // 月周期自定义
  const [monthPeriodType, setMonthPeriodType] = useState<MonthPeriodType>(MonthPeriodType.CUSTOM);
  const [monthPeriodStart, setMonthPeriodStart] = useState<number>(26); // 起始日
  const [monthPeriodEnd, setMonthPeriodEnd] = useState<number>(25); // 结束日
  const [monthPeriodVisible, setMonthPeriodVisible] = useState<boolean>(false);

  // 获取配置详情
  const { data: configData } = useRequest(() => apiV2EnergyReportTotalConfigDetailPost({}));

  // 部门选项
  const deptOptions = useMemo(() => {
    if (!configData?.deptConfigs?.length) return [];
    const nameMap = new Map<string, number>();
    configData.deptConfigs.forEach(item => {
      if (item.name && item.deptAreaId && !nameMap.has(item.name)) {
        nameMap.set(item.name, item.deptAreaId);
      }
    });
    return Array.from(nameMap.entries()).map(([label, value]) => ({ label, value }));
  }, [configData]);

  // 区域选项
  const areaOptions = useMemo(() => {
    if (!configData?.areaConfigs?.length) return [];
    const nameMap = new Map<string, number>();
    configData.areaConfigs.forEach(item => {
      if (item.name && item.deptAreaId && !nameMap.has(item.name)) {
        nameMap.set(item.name, item.deptAreaId);
      }
    });
    return Array.from(nameMap.entries()).map(([label, value]) => ({ label, value }));
  }, [configData]);

  // 处理报表范围切换（直接设置默认值，避免 useEffect 导致的二次请求）
  const handleReportScopeChange = useCallback(
    (newScope: ReportScope) => {
      setReportScope(newScope);
      // 切换到全厂时清空选择
      if (newScope === ReportScope.FACTORY) {
        setSelectedDeptAreaId(undefined);
      }
      // 切换到部门时
      else if (newScope === ReportScope.DEPT) {
        // 有数据则自动选择第一个，没有数据则清空
        setSelectedDeptAreaId(deptOptions.length > 0 ? deptOptions[0].value : undefined);
      }
      // 切换到区域时
      else if (newScope === ReportScope.AREA) {
        // 有数据则自动选择第一个，没有数据则清空
        setSelectedDeptAreaId(areaOptions.length > 0 ? areaOptions[0].value : undefined);
      }
    },
    [deptOptions, areaOptions]
  );

  // 当选择了部门或区域时，调用月周期详情接口获取配置
  useRequest(
    async () => {
      if (!selectedDeptAreaId) return null;
      return await apiV2EnergyReportMonthCycleDetailPost({ deptAreaId: selectedDeptAreaId });
    },
    {
      ready: !!selectedDeptAreaId,
      refreshDeps: [selectedDeptAreaId],
      onSuccess: res => {
        if (res) {
          setMonthPeriodType(res.type === 1 ? MonthPeriodType.NATURAL : MonthPeriodType.CUSTOM);
          setMonthPeriodStart(res.fromDay ?? 26);
          setMonthPeriodEnd(res.toDay ?? 25);
        } else {
          // 没有配置时使用默认值
          setMonthPeriodType(MonthPeriodType.CUSTOM);
          setMonthPeriodStart(26);
          setMonthPeriodEnd(25);
        }
      },
    }
  );

  // 月周期上下文值（即使 selectedDeptAreaId 为空也传递，子组件根据 reportScope 判断）
  const monthCycleApiParams = useMemo(
    () => ({
      deptAreaId: selectedDeptAreaId, // 可能为 undefined（部门/区域没有数据时）
      monthCycleType: monthPeriodType === MonthPeriodType.NATURAL ? 1 : 2,
      monthCycleFrom: monthPeriodStart,
      monthCycleTo: monthPeriodEnd,
    }),
    [selectedDeptAreaId, monthPeriodType, monthPeriodStart, monthPeriodEnd]
  );

  const getMonthPeriodRangeCallback = useCallback(
    (monthDate: Dayjs) =>
      getMonthPeriodRange(monthDate, monthPeriodStart, monthPeriodEnd, monthPeriodType === MonthPeriodType.NATURAL),
    [monthPeriodStart, monthPeriodEnd, monthPeriodType]
  );

  const getYearPeriodRangeCallback = useCallback(
    (yearDate: Dayjs) =>
      getYearPeriodRange(yearDate, monthPeriodStart, monthPeriodEnd, monthPeriodType === MonthPeriodType.NATURAL),
    [monthPeriodStart, monthPeriodEnd, monthPeriodType]
  );

  // 全厂时按月/按年强制走自然月、自然年（月初到月末、年初到年末），
  // 不走月周期自定义；仅部门/区域才走自定义周期。
  const effectiveMonthPeriodType = reportScope === ReportScope.FACTORY ? MonthPeriodType.NATURAL : monthPeriodType;

  const monthPeriodContext = useMemo(
    () => ({
      reportScope,
      selectedDeptAreaId,
      monthPeriodType: effectiveMonthPeriodType,
      monthPeriodStart,
      monthPeriodEnd,
      getMonthPeriodRange: getMonthPeriodRangeCallback,
      getYearPeriodRange: getYearPeriodRangeCallback,
      monthCycleApiParams,
    }),
    [
      reportScope,
      selectedDeptAreaId,
      effectiveMonthPeriodType,
      monthPeriodStart,
      monthPeriodEnd,
      getMonthPeriodRangeCallback,
      getYearPeriodRangeCallback,
      monthCycleApiParams,
    ]
  );

  // 时间分辨率和日期选择（用于 Cost 组件）
  const [timeResolution, setTimeResolution] = useState<DateType>(DateType.DAY);
  const [costDateValue, setCostDateValue] = useState<RangeValue>();

  const onChangePicker = useCallback(
    (dates: RangeValue) => {
      let isFlag = true;
      switch (timeResolution) {
        case DateType.DAY:
          if (dates?.[0] && dates?.[1] && dates?.[0].add(365, 'day').valueOf() < dates?.[1].valueOf()) {
            message.error('最多选择 365 天');
            isFlag = false;
            return;
          }
          break;
        case DateType.MONTH:
          if (dates?.[0] && dates?.[1] && dates?.[0].add(12, 'month').valueOf() < dates?.[1].valueOf()) {
            message.error('最多选择 12 个月');
            isFlag = false;
            return;
          }
          break;
        case DateType.YEAR:
          if (dates?.[0] && dates?.[1] && dates?.[0].add(6, 'year').valueOf() < dates?.[1].valueOf()) {
            message.error('最多选择 6 年');
            isFlag = false;
            return;
          }
          break;
      }

      if (isFlag) {
        setCostDateValue(dates);
      }
    },
    [timeResolution]
  );

  // Overview 组件日期变化时，更新趋势图的时间选择器
  const onOverviewDateChange = useCallback((dates: [Dayjs | null, Dayjs | null]) => {
    const [startDate, endDate] = dates;
    if (!startDate || !endDate) return;

    // 趋势图始终按天维度显示
    setTimeResolution(DateType.DAY);
    setCostDateValue([startDate, endDate]);
  }, []);

  const picker = useMemo(() => {
    if (timeResolution === DateType.MONTH) {
      return 'month';
    } else if (timeResolution === DateType.YEAR) {
      return 'year';
    } else {
      return 'date';
    }
  }, [timeResolution]);

  const disabledDate = useCallback(
    (current: Dayjs) => {
      if (picker === 'date') {
        return current && current >= dayjs().startOf('day');
      }
      return current && current > dayjs();
    },
    [picker]
  );

  React.useEffect(() => {
    if (timeResolution) {
      switch (timeResolution) {
        case DateType.DAY:
          setCostDateValue([dayjs().startOf('year'), dayjs().subtract(1, 'day')]);
          break;
        case DateType.MONTH:
          setCostDateValue([dayjs().subtract(11, 'month'), dayjs()]);
          break;
        case DateType.YEAR:
          setCostDateValue([dayjs().subtract(2, 'year'), dayjs()]);
          break;
      }
    }
  }, [timeResolution]);
  const handleClick = (
    e: React.MouseEvent<HTMLElement>,
    link: {
      title: React.ReactNode;
      href: string;
    }
  ) => {
    e.preventDefault();
    const id = link.href.replace('#', '');
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const onFinish = () => {
    form
      .validateFields()
      .then(values => {
        const { date } = values;
        // console.log('date', date);
        apiV2EnergyReportCostCalcPost({
          startDate: date[0].format('YYYY-MM-DD'),
          endDate: date[1].format('YYYY-MM-DD'),
        }).then(res => {
          setCalcModalOpen(false);
        });
      })
      .catch(err => {});
  };

  const calcModalDisabledDate = (current: Dayjs) => {
    if (!calcModalDates) {
      return false;
    }
    const tooLate = calcModalDates[0] && current.diff(calcModalDates[0], 'months') >= 6;
    const tooEarly = calcModalDates[1] && calcModalDates[1].diff(current, 'months') >= 6;
    const afterToday = current && current >= dayjs().startOf('day');
    return !!tooEarly || !!tooLate || !!afterToday;
  };

  const calcModalOnOpenChange = (open: boolean) => {
    if (open) {
      setCalcModalDates([null, null]);
    } else {
      setCalcModalDates(null);
    }
  };

  return (
    <Wrapper routes={breadcrumbRoutes?.routes} className={styles.wrapper}>
      <div className={styles.top}>
        <div className={styles.title_wrapper}>
          <FormTitle
            style={{ width: '100%', margin: 0 }}
            title="用能总量统计报表"
            extraContent={
              <Space size={8}>
                {reportScope === ReportScope.FACTORY && (
                  <Button type="primary" onClick={() => window.open(`/api/v2/energy-report/generate`)}>
                    导出报表
                  </Button>
                )}
                {hasRecalPermission && (
                  <Button type="primary" onClick={() => setCalcModalOpen(true)}>
                    重计算
                  </Button>
                )}
                {hasConfigPermission && (
                  <Button type="primary" onClick={() => window.open('/energy/statistic/report/config')}>
                    配置
                  </Button>
                )}
              </Space>
            }
          />
          {/* 报表范围选择 - 绝对定位紧跟标题 */}
          <div className={styles.scope_selector}>
            <Radio.Group
              onChange={(e: RadioChangeEvent) => handleReportScopeChange(e.target.value)}
              value={reportScope}
              buttonStyle="solid"
            >
              <Radio.Button value={ReportScope.FACTORY}>全厂</Radio.Button>
              <Radio.Button value={ReportScope.DEPT}>部门</Radio.Button>
              <Radio.Button value={ReportScope.AREA}>区域</Radio.Button>
            </Radio.Group>
            {reportScope === ReportScope.DEPT && (
              <Select
                style={{ width: 260 }}
                placeholder="请选择部门"
                showSearch
                optionFilterProp="label"
                options={deptOptions}
                value={selectedDeptAreaId}
                onChange={value => setSelectedDeptAreaId(value)}
              />
            )}
            {reportScope === ReportScope.AREA && (
              <Select
                style={{ width: 260 }}
                placeholder="请选择区域"
                showSearch
                optionFilterProp="label"
                options={areaOptions}
                value={selectedDeptAreaId}
                onChange={value => setSelectedDeptAreaId(value)}
              />
            )}
          </div>
        </div>
      </div>
      <div className={styles.bottom}>
        <div className={styles.LeftColStyle}>
          <Anchor
            getContainer={() => rightColRef.current || window}
            // getContainer={() =>  document.querySelector(`.${styles.RightColStyle}`) as HTMLElement}
            className={styles.custom_anchor}
            onClick={handleClick}
            items={[
              {
                key: 'part-overview',
                href: '#part-overview',
                title: <span className={styles.label}>总览</span>,
              },
              {
                key: 'part-cost',
                href: '#part-cost',
                title: <span className={styles.label}>总成本详情</span>,
              },
              {
                key: 'part-elec',
                href: '#part-elec',
                title: <span className={styles.label}>用电详情</span>,
              },
              {
                key: 'part-water',
                href: '#part-water',
                title: <span className={styles.label}>用水详情</span>,
              },
              {
                key: 'part-air',
                href: '#part-air',
                title: <span className={styles.label}>用气详情</span>,
              },
              {
                key: 'part-steam',
                href: '#part-steam',
                title: null, // 蒸汽详情锚点不显示
              },
            ]}
          />
        </div>
        <div className={styles.RightColStyle} ref={rightColRef}>
          <MonthPeriodContext.Provider value={monthPeriodContext}>
            {/* 总览 */}
            <div id="part-overview" style={{ paddingTop: 20 }}>
              <Overview
                setHasConfigGas={setHasConfigGas}
                onDateChange={onOverviewDateChange}
                reportScope={reportScope}
                selectedDeptAreaId={selectedDeptAreaId}
              />
            </div>
            {/* 时间选择器 - 总成本详情上方 */}
            <div className={styles.costDateSelector}>
              <Radio.Group
                onChange={(e: RadioChangeEvent) => {
                  setTimeResolution(e.target.value);
                }}
                value={timeResolution}
                buttonStyle="solid"
              >
                <Radio.Button value={DateType.DAY}>{DateTypeDisplay[DateType.DAY]}</Radio.Button>
                <Radio.Button value={DateType.MONTH}>{DateTypeDisplay[DateType.MONTH]}</Radio.Button>
                <Radio.Button value={DateType.YEAR}>{DateTypeDisplay[DateType.YEAR]}</Radio.Button>
              </Radio.Group>
              <RangePicker
                picker={picker}
                value={costDateValue}
                disabledDate={disabledDate}
                onChange={dates => onChangePicker(dates)}
                allowClear={false}
              />
              {/* 按月周期自定义 - 仅在部门/区域且按月/按年时显示 */}
              {(reportScope === ReportScope.DEPT || reportScope === ReportScope.AREA) &&
                (timeResolution === DateType.MONTH || timeResolution === DateType.YEAR) && (
                  <>
                    <Button type="link" onClick={() => setMonthPeriodVisible(true)}>
                      月周期设置
                    </Button>
                    <span style={{ color: 'var(--mx-text-secondary-color)', fontSize: 13 }}>
                      当前周期：
                      {monthPeriodType === MonthPeriodType.NATURAL
                        ? '自然月'
                        : `上月${monthPeriodStart}日至本月${monthPeriodEnd}日`}
                    </span>
                    <MonthCycleModal
                      open={monthPeriodVisible}
                      deptAreaId={selectedDeptAreaId}
                      deptOrArea={reportScope === ReportScope.DEPT ? 1 : 2}
                      monthCycleType={monthPeriodType === MonthPeriodType.NATURAL ? 1 : 2}
                      monthCycleFrom={monthPeriodStart}
                      monthCycleTo={monthPeriodEnd}
                      onCancel={() => setMonthPeriodVisible(false)}
                      onSaveSuccess={(type, fromDay, toDay) => {
                        setMonthPeriodType(type === 1 ? MonthPeriodType.NATURAL : MonthPeriodType.CUSTOM);
                        setMonthPeriodStart(fromDay);
                        setMonthPeriodEnd(toDay);
                      }}
                    />
                  </>
                )}

              {/* 导出按钮 - 仅在部门/区域时显示 */}
              {(reportScope === ReportScope.DEPT || reportScope === ReportScope.AREA) && (
                <Button
                  type="primary"
                  onClick={() => {
                    let fromTime: number;
                    let toTime: number;

                    // 计算时间范围（与详情组件保持一致）
                    if (timeResolution === DateType.DAY) {
                      fromTime = costDateValue?.[0]?.startOf('day').valueOf() ?? 0;
                      toTime = costDateValue?.[1]?.endOf('day').valueOf() ?? 0;
                    } else {
                      const timeRange = getTimeRangeByMonthPeriod(
                        timeResolution,
                        costDateValue?.[0],
                        costDateValue?.[1],
                        monthPeriodType,
                        monthPeriodStart,
                        monthPeriodEnd
                      );
                      fromTime = timeRange.startTime;
                      toTime = timeRange.endTime;
                    }

                    const query: any = {
                      fromTime,
                      toTime,
                      resolution: timeResolution,
                      deptAreaId: selectedDeptAreaId,
                      deptOrArea: reportScope === ReportScope.DEPT ? 1 : 2,
                      timestamp: Date.now(), // 添加时间戳字段
                    };

                    // 仅按月/按年时传月周期参数
                    if (timeResolution === DateType.MONTH || timeResolution === DateType.YEAR) {
                      query.monthCycleType = monthPeriodType === MonthPeriodType.NATURAL ? 1 : 2;
                      // 自定义周期时传起止日
                      if (monthPeriodType === MonthPeriodType.CUSTOM) {
                        query.monthCycleFrom = monthPeriodStart;
                        query.monthCycleTo = monthPeriodEnd;
                      }
                    }

                    window.open(`/api/v2/energy-report/export/dept-area?${qs.stringify(query)}`, '_blank');
                  }}
                >
                  导出
                </Button>
              )}
            </div>
            {/* 总成本详情 */}
            <div id="part-cost">
              <Cost timeResolution={timeResolution} dateValue={costDateValue} />
            </div>
            {/* 用电详情 */}
            <div id="part-elec">
              <Electricity timeResolution={timeResolution} dateValue={costDateValue} />
            </div>
            {/* 用水详情 */}
            <div id="part-water">
              <Water timeResolution={timeResolution} dateValue={costDateValue} />
            </div>
            {/* 用气详情 */}
            <div id="part-air">
              <Air gasList={hasConfigGas} timeResolution={timeResolution} dateValue={costDateValue} />
            </div>
            {/* 蒸汽详情 - 仅全厂范围且日期 <= 2026 年时显示 */}
            {reportScope === ReportScope.FACTORY && costDateValue?.[1] && costDateValue[1].year() <= 2026 && (
              <div id="part-steam">
                <Steam timeResolution={timeResolution} dateValue={costDateValue} />
              </div>
            )}
          </MonthPeriodContext.Provider>
        </div>
      </div>
      {/* 重计算弹窗 */}
      <Modal
        title="重计算"
        forceRender
        className={styles.calcModalBox}
        open={calcModalOpen}
        afterClose={() => form.resetFields()}
        onCancel={() => setCalcModalOpen(false)}
        contentClassName="modal-form-content"
        footer={[
          <Button key="back" onClick={() => setCalcModalOpen(false)} className={styles.cancelBtn}>
            取消
          </Button>,
          <Button key="submit" type="primary" onClick={onFinish}>
            确定
          </Button>,
        ]}
      >
        <div className={styles.modalBody}>
          <div className={styles.tipWrapper}>
            <ExclamationCircleOutlined style={{ color: 'var(--mx-warning-color)', fontSize: 12 }} />
            <span className={styles.txt}>重计算数值会覆盖现有数据</span>
          </div>

          <Form form={form}>
            <Form.Item label="选择时间" name="date" rules={[{ required: true, message: '请选择时间' }]}>
              <RangePicker
                value={calcModalValue || calcModalDates}
                disabledDate={calcModalDisabledDate}
                onCalendarChange={val => {
                  setCalcModalDates(val);
                }}
                onChange={val => {
                  setCalcModalValue(val);
                }}
                onOpenChange={calcModalOnOpenChange}
              />
            </Form.Item>
          </Form>
        </div>
      </Modal>
    </Wrapper>
  );
};

export default EnergyStatisticalReport;
